var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { parseExpression } from 'cron-parser';
import { DateTime } from 'luxon';
import { defaults } from '../../defaults';
import { Log } from '../../log';
import { Sutl } from '../../sutl';
import { Timeouts } from './drivers/framework/Timeouts';
import { ParseError, Parser } from './expression-evaluator';
const debounceRoomStatesTimeMs = 3000;
const log = Log.channel('AutomationServiceImpl');
export class AutomationServiceImpl {
    constructor(rootScope, kayleeService, roomService) {
        this.kayleeService = kayleeService;
        this.roomService = roomService;
        this.isRunning = true;
        this.timeouts = new Timeouts();
        this.displayMotionAutomation = new DisplayMotionAutomation();
        this.roomPowerOffAutomation = new RoomPowerOffAutomation(rootScope, this.roomService, this.timeouts);
        this.automationSchedule = new AutomationSchedule(this.roomService, this.timeouts, (macroId) => {
            const room = this.roomService.getRoom();
            if (room && this.roomService.online())
                room.$requestRunRoomMacro(macroId);
        });
        this.automationTrigger = new AutomationTrigger(this.timeouts, (macroId) => {
            const room = this.roomService.getRoom();
            if (room && this.roomService.online())
                room.$requestRunRoomMacro(macroId);
        });
        this.onRoomChanged = this.onRoomChanged.bind(this);
        this.onSiteChanged = this.onSiteChanged.bind(this);
        this.roomService.onOnlineChanged(this.onRoomServiceOnlineChanged.bind(this));
    }
    onRoomChanged() {
        const room = this.roomService.getRoom();
        if (!room || !this.roomService.online())
            return;
        this.displayMotionAutomation.update(room);
        const site = this.kayleeService.getSite(room.siteId);
        if (!site)
            return;
        this.automationSchedule.update(site, room);
        this.automationTrigger.update(room);
    }
    onRoomServiceOnlineChanged(online) {
        if (online) {
            const room = this.roomService.getRoom();
            if (!room) {
                log.error(`room undefined`);
                return;
            }
            room.$offChange(this.onRoomChanged);
            room.$onChange(this.onRoomChanged);
            const site = this.kayleeService.getSite(room.siteId);
            site.$offChange(this.onSiteChanged);
            site.$onChange(this.onSiteChanged);
        }
        else {
            const room = this.roomService.getRoom();
            if (room) {
                room.$offChange(this.onRoomChanged);
                const site = this.kayleeService.getSite(room.siteId);
                site === null || site === void 0 ? void 0 : site.$offChange(this.onSiteChanged);
            }
            this.displayMotionAutomation.reset();
            this.roomPowerOffAutomation.reset();
            this.automationSchedule.reset();
            this.automationTrigger.reset();
        }
    }
    onSiteChanged() {
        if (!this.roomService.online())
            return;
        const room = this.roomService.getRoom();
        if (!room)
            return;
        const site = this.kayleeService.getSite(room.siteId);
        if (!site)
            return;
        this.roomPowerOffAutomation.update(site);
        this.automationSchedule.update(site, room);
    }
    triggerEvent(event) {
        const room = this.roomService.getRoom();
        if (!room || !this.roomService.online())
            return;
        this.automationTrigger.update(room, event);
    }
}
class DisplayMotionAutomation {
    constructor() {
        this.roomOnlineTime = 0;
    }
    reset() {
        this.displaysState = undefined;
    }
    update(room) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        return __awaiter(this, void 0, void 0, function* () {
            const commandRequests = [];
            const newRoomOnlineState = (_a = room.states) === null || _a === void 0 ? void 0 : _a.onlineState;
            const newDisplaysState = Sutl.deepCopy((_b = room.states) === null || _b === void 0 ? void 0 : _b.displays);
            const prevRoomOnlineState = this.roomOnlineState;
            if (newRoomOnlineState !== 'offline') {
                if (prevRoomOnlineState === 'offline')
                    this.roomOnlineTime = Date.now();
                const roomOnlineDurationMs = Date.now() - this.roomOnlineTime;
                for (const [displayId, newDisplayState] of Object.entries(newDisplaysState !== null && newDisplaysState !== void 0 ? newDisplaysState : {})) {
                    const prevDisplayState = (_c = this.displaysState) === null || _c === void 0 ? void 0 : _c[displayId];
                    if (roomOnlineDurationMs > 2000 &&
                        (prevDisplayState === null || prevDisplayState === void 0 ? void 0 : prevDisplayState.power) !== undefined &&
                        (newDisplayState === null || newDisplayState === void 0 ? void 0 : newDisplayState.power) !== undefined &&
                        newDisplayState.power !== prevDisplayState.power) {
                        const displayMotions = (_g = (_f = (_e = (_d = room.caps) === null || _d === void 0 ? void 0 : _d.displays) === null || _e === void 0 ? void 0 : _e[displayId]) === null || _f === void 0 ? void 0 : _f.displayMotion) !== null && _g !== void 0 ? _g : {};
                        for (const [displayMotionId, displayMotion] of Object.entries(displayMotions)) {
                            if (newDisplayState.power &&
                                ((_j = (_h = displayMotion.controls) === null || _h === void 0 ? void 0 : _h.show) === null || _j === void 0 ? void 0 : _j.automateWithPower)) {
                                commandRequests.push(room.$requestShowDisplay(displayId, displayMotionId));
                            }
                            else if (!newDisplayState.power &&
                                ((_l = (_k = displayMotion.controls) === null || _k === void 0 ? void 0 : _k.hide) === null || _l === void 0 ? void 0 : _l.automateWithPower)) {
                                commandRequests.push(room.$requestHideDisplay(displayId, displayMotionId));
                            }
                        }
                    }
                }
            }
            this.displaysState = newDisplaysState;
            this.roomOnlineState = newRoomOnlineState;
            yield Promise.all(commandRequests);
        });
    }
}
class RoomPowerOffAutomation {
    constructor(rootScope, roomService, timeouts) {
        this.rootScope = rootScope;
        this.roomService = roomService;
        this.dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        this.maxRoomPowerOffRetries = 3;
        this.roomPowerOffRetries = 0;
        this.timeZone = defaults.inara.timeZone;
        this.onRoomPowerOffTimeout = this.onRoomPowerOffTimeout.bind(this);
        this.roomPowerOffTimeout = timeouts.create();
    }
    onRoomPowerOffTimeout() {
        var _a;
        if (!this.roomService.online()) {
            log.error(`room offline`);
            return;
        }
        const room = this.roomService.getRoom();
        if (!room) {
            log.error(`room undefined`);
            return;
        }
        if ((_a = room.states) === null || _a === void 0 ? void 0 : _a.onAir) {
            if (this.roomPowerOffRetries >= this.maxRoomPowerOffRetries) {
                log.info(`room is in a call, but maximum retry limit reached; giving up`);
                this.scheduleRoomPowerOff();
            }
            else {
                this.roomPowerOffRetries++;
                log.info(`room is in a call, rescheduling room power off for one hour from now (attempt ${this.roomPowerOffRetries})`);
                this.roomPowerOffTimeout.set(this.onRoomPowerOffTimeout, 60 * 60 * 1000);
            }
        }
        else {
            log.info(`requesting room power off`);
            this.rootScope.$broadcast('RequestRoomPowerOffConfirm');
            this.scheduleRoomPowerOff();
        }
    }
    reset() {
        this.roomPowerOffRetries = 0;
        this.roomPowerOffTimeout.clear();
        this.schedule = undefined;
        this.timeZone = defaults.inara.timeZone;
    }
    scheduleRoomPowerOff() {
        this.roomPowerOffTimeout.clear();
        this.roomPowerOffRetries = 0;
        if (!this.schedule) {
            log.info(`room power off schedule: none`);
            return;
        }
        log.info('room power off schedule:');
        log.info('day time  next                  sec until');
        log.info('--- ----- --------------------- ---------');
        const now = DateTime.now().setZone(this.timeZone);
        let next = now.plus({ days: 100 });
        for (const entry of Object.values(this.schedule)) {
            const day = now.weekday == 7 ? 0 : now.weekday; // moment -> luxon
            const dayOffset = entry.day >= day ? entry.day - day : entry.day + 7 - day;
            let entryDateTime = now
                .startOf('day')
                .plus({ days: dayOffset })
                .plus({ hours: entry.hour })
                .plus({ minutes: entry.minute });
            if (entryDateTime.valueOf() <= now.valueOf()) {
                entryDateTime = entryDateTime.plus({ days: 7 });
            }
            let msg = this.dayNames[entry.day] + ' ';
            msg += entry.hour.toString().padStart(2, '0') + ':';
            msg += entry.minute.toString().padStart(2, '0') + ' ';
            msg += entryDateTime.toFormat('yyyy-MM-dd hh:mm:ss a') + ' ';
            msg += Math.round((entryDateTime.valueOf() - now.valueOf()) / 1000)
                .toString()
                .padStart(9, ' ');
            log.info(msg);
            if (entryDateTime.valueOf() - next.valueOf() < 0)
                next = entryDateTime;
        }
        const timeoutMs = next.valueOf() - DateTime.now().valueOf();
        log.info(`scheduling room power off for ${timeoutMs} ms from now`);
        this.roomPowerOffTimeout.set(this.onRoomPowerOffTimeout, timeoutMs);
    }
    update(site) {
        var _a, _b, _c;
        const newSchedule = (_a = site.settings) === null || _a === void 0 ? void 0 : _a.roomPowerOffSchedule;
        const newTimeZone = (_c = (_b = site.settings) === null || _b === void 0 ? void 0 : _b.timeZone) !== null && _c !== void 0 ? _c : defaults.inara.timeZone;
        if (this.schedule !== undefined &&
            Sutl.objEquals(newSchedule, this.schedule) &&
            this.timeZone === newTimeZone) {
            return;
        }
        this.schedule = newSchedule;
        this.timeZone = newTimeZone;
        this.scheduleRoomPowerOff();
    }
}
class AutomationSchedule {
    constructor(roomService, timeouts, runRoomMacro) {
        this.roomService = roomService;
        this.runRoomMacro = runRoomMacro;
        this.nextAutomationTimestamps = {};
        this.timeZone = defaults.inara.timeZone;
        this.onNextAutomationTimeout = this.onNextAutomationTimeout.bind(this);
        this.nextAutomationTimeout = timeouts.create();
    }
    onNextAutomationTimeout() {
        var _a, _b;
        if (!this.roomService.online()) {
            log.error(`room offline`);
            return;
        }
        const room = this.roomService.getRoom();
        if (!room) {
            log.error(`room undefined`);
            return;
        }
        const automations = Object.entries((_a = this.automations) !== null && _a !== void 0 ? _a : {}).sort((a, b) => {
            var _a, _b;
            return ((_a = this.nextAutomationTimestamps[a[0]]) !== null && _a !== void 0 ? _a : Number.MAX_VALUE) -
                ((_b = this.nextAutomationTimestamps[b[0]]) !== null && _b !== void 0 ? _b : Number.MAX_VALUE);
        });
        for (const [key, automation] of automations) {
            if (automation.type === 'schedule') {
                if (((_b = this.nextAutomationTimestamps[key]) !== null && _b !== void 0 ? _b : Number.MAX_VALUE) <= Date.now()) {
                    log.info(`running scheduled automation: "${automation.name}" (${key})`);
                    if (automation.action.type === 'roomMacro') {
                        this.runRoomMacro(automation.action.macroId);
                    }
                }
            }
        }
        this.scheduleNextAutomation();
    }
    reset() {
        this.automations = undefined;
        this.nextAutomationTimeout.clear();
        this.timeZone = defaults.inara.timeZone;
    }
    scheduleNextAutomation() {
        var _a;
        this.nextAutomationTimeout.clear();
        this.nextAutomationTimestamps = {};
        const now = DateTime.now().setZone(this.timeZone);
        //let next = now.plus({ days: 100 });
        let next;
        const automations = Object.entries((_a = this.automations) !== null && _a !== void 0 ? _a : {}).sort((a, b) => a[0].localeCompare(b[0]));
        const messages = [];
        for (const [key, entry] of automations) {
            if (entry.type !== 'schedule')
                continue;
            let automationDateTime;
            let unscheduledReason = 'invalid cron expression';
            if (entry.endTime && entry.endTime <= now.valueOf()) {
                unscheduledReason = 'past end time';
            }
            else if (entry.startTime && entry.startTime > now.valueOf()) {
                const interval = parseExpression(entry.cronExpression, {
                    currentDate: entry.startTime,
                    tz: this.timeZone,
                });
                try {
                    automationDateTime = DateTime.fromJSDate(new Date(interval.next().getTime()));
                }
                catch (_b) { }
            }
            else {
                const interval = parseExpression(entry.cronExpression, { tz: this.timeZone });
                try {
                    automationDateTime = DateTime.fromJSDate(new Date(interval.next().getTime()));
                }
                catch (_c) { }
            }
            const name = entry.name.padEnd(25, ' ');
            const cronStr = entry.cronExpression.padEnd(15, ' ');
            if (automationDateTime === undefined) {
                messages.push(`${name} ${cronStr} ${unscheduledReason}`);
                continue;
            }
            const nextStr = automationDateTime.toFormat('yyyy-MM-dd hh:mm:ss a');
            const secUntil = Math.round((automationDateTime.valueOf() - now.valueOf()) / 1000);
            messages.push(`${name} ${cronStr} ${nextStr} ${secUntil.toString().padStart(9, ' ')}`);
            this.nextAutomationTimestamps[key] = automationDateTime.valueOf();
            if (next === undefined || automationDateTime.valueOf() < next.valueOf()) {
                next = automationDateTime;
            }
        }
        if (messages.length > 0) {
            log.info('automation schedule:');
            log.info('name                      cron expression next                   sec until');
            log.info('------------------------- --------------- ---------------------- ---------');
            messages.forEach(m => log.info(m));
        }
        else {
            log.info(`automation schedule: none`);
        }
        if (next !== undefined) {
            const timeoutMs = next.valueOf() - now.valueOf();
            log.info(`scheduling next automation for ${timeoutMs} ms from now`);
            this.nextAutomationTimeout.set(this.onNextAutomationTimeout, timeoutMs);
        }
        else {
            log.info(`no automation to schedule`);
        }
    }
    update(site, room) {
        var _a, _b, _c, _d;
        const newAutomations = (_b = (_a = room.settings) === null || _a === void 0 ? void 0 : _a.automations) !== null && _b !== void 0 ? _b : {};
        const newTimeZone = (_d = (_c = site.settings) === null || _c === void 0 ? void 0 : _c.timeZone) !== null && _d !== void 0 ? _d : defaults.inara.timeZone;
        if (this.automations !== undefined &&
            Sutl.objEquals(newAutomations, this.automations) &&
            this.timeZone === newTimeZone) {
            return;
        }
        this.automations = Sutl.deepCopy(newAutomations);
        this.timeZone = newTimeZone;
        this.scheduleNextAutomation();
    }
}
class AutomationTrigger {
    constructor(timeouts, runRoomMacro) {
        this.runRoomMacro = runRoomMacro;
        this.automations = {};
        this.triggers = {};
        this.debounceRoomStatesTimeout = timeouts.create();
    }
    reset() {
        this.automations = {};
        this.caps = undefined;
        this.debounceRoomStatesTimeout.clear();
        this.savedStates = undefined;
        this.states = undefined;
        this.triggers = {};
    }
    getChangedGetter(prevObj, currObj, fnName) {
        return (path) => {
            if (typeof path !== 'string' && !Array.isArray(path)) {
                throw new Error(`invalid path argument to ${fnName}: path = ${JSON.stringify(path)}`);
            }
            return Sutl.getNode(prevObj, path) !== Sutl.getNode(currObj, path);
        };
    }
    getChangedToGetter(prevObj, currObj, fnName) {
        return (path, value) => {
            if (typeof path !== 'string' && !Array.isArray(path)) {
                throw new Error(`invalid path argument to ${fnName}`);
            }
            const curr = Sutl.getProp(currObj, path);
            return Sutl.getProp(prevObj, path) !== curr && curr === value;
        };
    }
    getLookerUpper(obj, fnName) {
        return (path, prop, value) => {
            log.warning(`capsLookupId is deprecated and will be removed; update your automations immediately`);
            if (typeof path !== 'string' && !Array.isArray(path)) {
                throw new Error(`invalid path argument to ${fnName}`);
            }
            if (typeof prop !== 'string') {
                throw new Error(`invalid key argument to ${fnName}`);
            }
            const collection = Sutl.getProp(obj, path);
            if (typeof collection !== 'object' ||
                Array.isArray(collection) ||
                collection === null) {
                throw new Error(`no collection at path in ${fnName}`);
            }
            for (const [key, item] of Object.entries(collection)) {
                if (Sutl.objEquals(Sutl.getProp(item, prop), value))
                    return key;
            }
            return '';
        };
    }
    getValueGetter(obj, fnName) {
        return (path) => {
            if (typeof path !== 'string' && !Array.isArray(path)) {
                throw new Error(`invalid path argument to ${fnName}: path = ${JSON.stringify(path)}`);
            }
            return Sutl.getNode(obj, path);
        };
    }
    update(room, event) {
        var _a, _b, _c, _d, _e, _f;
        const newAutomations = (_b = (_a = room.settings) === null || _a === void 0 ? void 0 : _a.automations) !== null && _b !== void 0 ? _b : {};
        if (!Sutl.objEquals(newAutomations, this.automations)) {
            this.automations = Sutl.deepCopy(newAutomations);
            this.triggers = {};
            for (const [key, automation] of Object.entries(newAutomations)) {
                if (automation.type !== 'trigger')
                    continue;
                const parser = new Parser(automation.triggerExpression);
                let ast;
                try {
                    ast = parser.parse();
                }
                catch (error) {
                    const prefix = `error parsing trigger expression for id "${key}"`;
                    if (error instanceof ParseError || error.position !== undefined) {
                        log.warning(`${prefix}: ${error.message} at position ${error.position}`);
                    }
                    else {
                        log.warning(`${prefix}: ${error.message}`);
                    }
                    continue;
                }
                this.triggers[key] = ast;
            }
        }
        if ((event === null || event === void 0 ? void 0 : event.type) === 'roomCapsChanged') {
            this.savedStates = Sutl.deepCopy((_c = room.states) !== null && _c !== void 0 ? _c : {});
            this.debounceRoomStatesTimeout.set(() => {
                const states = this.states;
                this.states = this.savedStates;
                this.savedStates = undefined;
                this.update({
                    companyId: room.companyId,
                    siteId: room.siteId,
                    name: room.name,
                    caps: this.caps,
                    states,
                });
            }, debounceRoomStatesTimeMs);
        }
        const newCaps = (_d = room.caps) !== null && _d !== void 0 ? _d : {};
        const prevCaps = this.caps;
        const capsChanged = !Sutl.objEquals(this.caps, newCaps);
        if (capsChanged)
            this.caps = Sutl.deepCopy(newCaps);
        const newStates = (_e = room.states) !== null && _e !== void 0 ? _e : {};
        const prevStates = this.states;
        const statesChanged = !Sutl.objEquals(this.states, newStates);
        if (this.debounceRoomStatesTimeout.isRunning && statesChanged) {
            this.debounceRoomStatesTimeout.restart(debounceRoomStatesTimeMs);
        }
        if (!this.debounceRoomStatesTimeout.isRunning && statesChanged) {
            this.states = Sutl.deepCopy(newStates);
        }
        if (this.debounceRoomStatesTimeout.isRunning && !capsChanged && event === undefined) {
            return;
        }
        if (!capsChanged && !statesChanged && event === undefined) {
            return;
        }
        const roomScope = {
            atob: atob,
            btoa: btoa,
            caps: this.getValueGetter(newCaps, 'caps'),
            capsChanged: this.getChangedGetter(prevCaps, newCaps, 'capsChanged'),
            capsLookupId: this.getLookerUpper(newCaps, 'capsLookupId'),
            event,
            now: Date.now(),
            prevCaps: this.getValueGetter(prevCaps, 'prevCaps'),
            prevStates: this.getValueGetter(prevStates, 'prevStates'),
            states: this.getValueGetter(newStates, 'states'),
            statesChanged: this.getChangedGetter(prevStates, newStates, 'statesChanged'),
            statesChangedTo: this.getChangedToGetter(prevStates, newStates, 'statesChangedTo'),
        };
        for (const [key, trigger] of Object.entries(this.triggers)) {
            const automation = this.automations[key];
            if ((automation === null || automation === void 0 ? void 0 : automation.type) !== 'trigger') {
                log.error(`unexpected trigger automation: ${JSON.stringify(automation)}`);
                continue;
            }
            if (automation.extraScope !== undefined) {
                log.warning(`extraScope is deprecated and will be removed; update your automations immediately`);
            }
            const scope = Object.assign(Object.assign({}, ((_f = automation.extraScope) !== null && _f !== void 0 ? _f : {})), roomScope);
            let triggered = false;
            try {
                triggered = Boolean(trigger.evaluate(scope));
            }
            catch (error) {
                const prefix = `error evaluating trigger expression for "${automation.name}" (${key})`;
                log.warning(`${prefix}: ${error.message}`);
            }
            if (triggered) {
                const automation = this.automations[key];
                if (automation.action.type !== 'roomMacro') {
                    log.error(`unexpected trigger action: ${JSON.stringify(automation)}`);
                    continue;
                }
                log.info(`running triggered automation: "${automation.name}" (${key})`);
                this.runRoomMacro(automation.action.macroId);
            }
        }
    }
}
